import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Loading,
  Message,
  toast,
} from '@workwhile/ui'
import styles from './CreateRuleFlowMain.module.css'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createRuleSchema, CreateRuleSchema } from '../schemas/createRule'
import { useCreateRuleFlowContextValue } from '../context/CreateRuleFlowProvider'
import { useRulesWorkflowsContextValue } from '../../../context/RulesWorkflowsProvider'
import { ModalSelect, ModalWithSelect } from 'components/ModalSelect'

const COVERAGE_SCOPE_OPTIONS = [
  { label: 'Full', value: 'full' as const },
  { label: 'Unincorporated only', value: 'unincorporated_only' as const },
]

type JurisdictionOption = { label: string; value: number }

export const CreateRuleFlowMain = () => {
  const { createRuleFlow } = useRulesWorkflowsContextValue()

  const {
    state: {
      jurisdictionOptions,
      jurisdictionsState: {
        isLoading: isJurisdictionsLoading,
        isError: isJurisdictionsError,
      },
      mutationState: { isPending, errorMessage },
    },
    actions: { submit },
  } = useCreateRuleFlowContextValue()

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateRuleSchema>({
    resolver: zodResolver(createRuleSchema),
    defaultValues: {
      effectiveStart: '',
      effectiveEnd: '',
      coverageScope: undefined,
      reference: '',
      jurisdictionIds: [],
    },
  })

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        effectiveStart: data.effectiveStart,
        effectiveEnd: data.effectiveEnd,
        coverageScope: data.coverageScope,
        reference: data.reference,
        jurisdictionIds: data.jurisdictionIds,
      },
      {
        onSuccess: () => {
          toast.success('Rule created successfully', { position: 'top-center' })
          reset()
          createRuleFlow.close()
        },
      }
    )
  })

  const handleClose = () => {
    reset()
    createRuleFlow.close()
  }

  return (
    <ModalWithSelect
      open={createRuleFlow.isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Create
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" className={styles.modalTitle}>
          Create rule
        </Heading>
        <Box mt={3}>
          <Field label="Effective start" error={errors.effectiveStart?.message}>
            <Input {...register('effectiveStart')} type="date" />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Effective end" error={errors.effectiveEnd?.message}>
            <Input {...register('effectiveEnd')} type="date" />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Coverage scope" error={errors.coverageScope?.message}>
            <Controller
              name="coverageScope"
              control={control}
              render={({ field }) => (
                <ModalSelect
                  options={COVERAGE_SCOPE_OPTIONS}
                  value={COVERAGE_SCOPE_OPTIONS.find(
                    (o) => o.value === field.value
                  )}
                  onChange={(
                    option: (typeof COVERAGE_SCOPE_OPTIONS)[number] | null
                  ) => field.onChange(option?.value)}
                  placeholder="Select coverage scope"
                />
              )}
            />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Reference" error={errors.reference?.message}>
            <Input {...register('reference')} />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Jurisdictions" error={errors.jurisdictionIds?.message}>
            {isJurisdictionsLoading ? (
              <Loading />
            ) : isJurisdictionsError ? (
              <Message variant="error" borderRadius="small">
                Failed to load jurisdictions.
              </Message>
            ) : (
              <Controller
                name="jurisdictionIds"
                control={control}
                render={({ field }) => (
                  <ModalSelect<JurisdictionOption, true>
                    isMulti
                    options={jurisdictionOptions}
                    value={jurisdictionOptions.filter((o) =>
                      (field.value ?? []).includes(o.value)
                    )}
                    onChange={(selected) =>
                      field.onChange(selected.map((o) => o.value))
                    }
                    placeholder="Search by name or ID..."
                    filterOption={(option, input) => {
                      const q = input.toLowerCase()
                      return (
                        option.label.toLowerCase().includes(q) ||
                        option.value.toString().includes(q)
                      )
                    }}
                  />
                )}
              />
            )}
          </Field>
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
